/**
 * In-memory store for the stop-to-lines mapping loaded from `/api/stops/lines`.
 */

import { useSyncExternalStore } from "react";

import type { GeocodeTransit, GtfsRoute } from "../types/dtos/api/geocode.dto";
import { fetchStopLinesMapping } from "./StopLinesApi";
import { resolveStopLinesMappingKey, type StopLinesLookupOptions } from "./stopLines.tools";

/** Load lifecycle for the stop-lines mapping. */
export type StopLinesLoadStatus = "idle" | "loading" | "ready" | "error";

type Listener = () => void;

/**
 * Singleton service that holds the stop-lines mapping in memory and exposes lookup helpers.
 */
class StopLinesService {

    private lines: Record<string, GeocodeTransit> = {};

    /** Normalized child stop key → station key redirects emitted by the backend. */
    private aliases: Record<string, string> = {};

    private status: StopLinesLoadStatus = "idle";

    private loadPromise: Promise<void> | null = null;

    private readonly listeners = new Set<Listener>();

    /**
     * Fetches and stores the stop-lines mapping. Concurrent calls share the same promise.
     *
     * @returns Promise resolved when the first load attempt finishes.
     */
    async load(): Promise<void> {
        if ( this.loadPromise !== null ) {
            return this.loadPromise;
        }

        this.status = "loading";
        this.emit();

        this.loadPromise = this.executeLoad();

        return this.loadPromise;
    }

    /**
     * @returns Whether the first load attempt has completed (success or failure).
     */
    isReady(): boolean {
        return this.status === "ready" || this.status === "error";
    }

    /**
     * @returns Current load lifecycle status.
     */
    getStatus(): StopLinesLoadStatus {
        return this.status;
    }

    /**
     * @param stopId Prefixed stop id from the spatial index or geocoder.
     * @param options Optional GTFS source and parent station hints.
     * @returns Transit lines for the stop, or `null` when not found.
     */
    getTransitForStop( stopId: string, options: StopLinesLookupOptions = {} ): GeocodeTransit | null {
        const mappingKey = resolveStopLinesMappingKey( stopId, options );

        if ( mappingKey === "" ) {
            return null;
        }

        /*
         * Direct hit first; otherwise follow the backend alias for child-only ids
         * (e.g. realtime `sibra_0:1ABE` whose station `1:PPT` is not id-derivable).
         */
        const resolvedKey = this.lines[mappingKey] !== undefined
            ? mappingKey
            : this.aliases[mappingKey];

        return resolvedKey !== undefined ? this.lines[resolvedKey] ?? null : null;
    }

    /**
     * @param stopId Prefixed stop id from the spatial index or geocoder.
     * @param options Optional GTFS source and parent station hints.
     * @returns Routes serving the stop, or an empty array when not found.
     */
    getRoutesForStop( stopId: string, options: StopLinesLookupOptions = {} ): GtfsRoute[] {
        return this.getTransitForStop( stopId, options )?.routes ?? [];
    }

    /**
     * @param stopId Prefixed stop id from the spatial index or geocoder.
     * @param options Optional GTFS source and parent station hints.
     * @returns Whether the mapping contains lines for the resolved stop key.
     */
    hasStop( stopId: string, options: StopLinesLookupOptions = {} ): boolean {
        return this.getTransitForStop( stopId, options ) !== null;
    }

    /**
     * Subscribes to load status changes.
     *
     * @param listener Callback invoked when status changes.
     * @returns Unsubscribe function.
     */
    subscribe( listener: Listener ): () => void {
        this.listeners.add( listener );

        return (): void => {
            this.listeners.delete( listener );
        };
    }

    private async executeLoad(): Promise<void> {
        try {
            const index = await fetchStopLinesMapping();
            this.lines = index.lines;
            this.aliases = index.aliases;
            this.status = "ready";

            console.log( `[StopLinesService] Loaded ${ Object.keys( this.lines ).length } stop line entries` );
        } catch( error: unknown ) {
            this.lines = {};
            this.aliases = {};
            this.status = "error";

            console.warn( "[StopLinesService] Failed to load stop lines mapping", error );
        } finally {
            this.emit();
        }
    }

    private emit(): void {
        for ( const listener of this.listeners ) {
            listener();
        }
    }

}

/** Shared stop-lines service instance. */
export const stopLinesService = new StopLinesService();

/**
 * React hook that returns whether the stop-lines mapping has finished loading.
 *
 * @returns `true` after the first load attempt completes.
 */
export const useStopLinesReady = (): boolean => {
    return useSyncExternalStore(
        ( listener ) => stopLinesService.subscribe( listener ),
        () => stopLinesService.isReady(),
        () => false,
    );
};
